import React, { useEffect, useRef, useState } from "react";
import { Storefront, ArrowSquareOut, Warning } from "@phosphor-icons/react";

// Phase 3G: WordPress catalog URL. Hardcoded constant — no env var to avoid config sprawl.
// If WordPress redirects to /client-login/ that is acceptable per spec.
const CATALOG_URL = "https://gardaj.com/shop/";

export default function Catalog() {
  const iframeRef = useRef(null);
  const [iframeStatus, setIframeStatus] = useState("loading"); // loading | loaded | timeout
  const openInNewTab = () => window.open(CATALOG_URL, "_blank", "noopener,noreferrer");

  useEffect(() => {
    // Detect blocked-by-X-Frame-Options / CSP: if iframe never fires `load` within 6s,
    // surface the helper banner. Most modern browsers still fire `load` when the response
    // is replaced by an empty document, so this is a soft hint, not a guarantee.
    const t = setTimeout(() => {
      setIframeStatus((s) => (s === "loading" ? "timeout" : s));
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="space-y-5" data-testid="catalog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Products</div>
          <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Storefront size={30} weight="duotone" className="text-[#1A3A21]" /> Catalog
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Internal catalog view. Product data and access are managed in WordPress.
          </p>
        </div>
        <a
          href={CATALOG_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="catalog-open-newtab"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03]"
        >
          <ArrowSquareOut size={15} weight="bold" /> Open catalog in new tab
        </a>
      </div>

      {iframeStatus === "timeout" && (
        <div
          data-testid="catalog-blocked-banner"
          className="bg-yellow-50 border border-yellow-200 text-yellow-900 text-xs rounded-md px-3 py-2 flex items-start gap-2"
        >
          <Warning weight="fill" size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            The catalog may be blocked from embedding by WordPress security headers. Use{" "}
            <button
              onClick={openInNewTab}
              className="underline font-semibold hover:text-yellow-700"
            >
              Open catalog in new tab
            </button>{" "}
            to access it directly.
          </div>
        </div>
      )}

      <div
        className="bg-white border border-gray-200 rounded-lg overflow-hidden"
        data-testid="catalog-iframe-wrap"
      >
        <iframe
          ref={iframeRef}
          src={CATALOG_URL}
          title="GARDAJ WordPress Catalog"
          data-testid="catalog-iframe"
          onLoad={() => setIframeStatus("loaded")}
          referrerPolicy="no-referrer-when-downgrade"
          loading="lazy"
          style={{ width: "100%", height: "75vh", minHeight: 480, border: 0 }}
        />
      </div>
    </div>
  );
}
